import { useEffect, useRef, useState } from 'react'
import { TextField } from '@mui/material'
import { SEARCH_DEBOUNCE_MS } from '@/hooks/useSearch'
import { useTableStore } from '@/store/table/table'
import { useAppTheme } from '@/theme/ThemeContext'
import FilterCard from './FilterCard'
import { textCodec } from './filterCodecs'
import type { FilterWidgetProps } from './filterKinds'

/**
 * A live search of one column. The text waits for a pause in the typing like the top search
 * box, then filters with CONTAINS. An empty field removes the filter. No list of values is
 * read or shown, because a site can hold 100,000 meters.
 */
const TextFilter = ({ field }: FilterWidgetProps) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { apiFilters, updateFieldFilter } = useTableStore()
  const stored = textCodec.fromApi(apiFilters.filter(f => f.name === field))
  const [text, setText] = useState(stored?.contains ?? '')
  const sent = useRef(text.trim())
  const unsent = useRef<string | null>(null)

  const send = (value: string) => {
    unsent.current = null
    if (value === sent.current) return
    sent.current = value
    updateFieldFilter(field, value ? [value] : [], 'CONTAINS')
  }
  const sendRef = useRef(send)
  sendRef.current = send

  useEffect(() => {
    const trimmed = text.trim()
    if (trimmed === sent.current) return
    unsent.current = trimmed
    const timer = window.setTimeout(() => sendRef.current(trimmed), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [text])

  // Closing the card inside the pause must not lose the typed text.
  useEffect(
    () => () => {
      if (unsent.current !== null) sendRef.current(unsent.current)
    },
    []
  )

  const clear = () => {
    unsent.current = null
    sent.current = ''
    setText('')
    updateFieldFilter(field, [])
  }

  return (
    <FilterCard
      title="חיפוש בעמודה"
      isActive={stored !== null || text.trim() !== ''}
      onClear={clear}
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        placeholder="חיפוש"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key !== 'Enter') return
          e.preventDefault()
          e.stopPropagation()
          send(text.trim())
        }}
        inputProps={{ dir: 'rtl', autoComplete: 'off', 'aria-label': 'חיפוש בעמודה' }}
        sx={{
          backgroundColor: isDarkMode ? '#0D192F' : '#F8FAFD',
          '& .MuiInputBase-input': { color: isDarkMode ? '#fff' : '#003F61' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDarkMode ? '#445876' : 'rgba(0,0,0,0.23)',
          },
        }}
      />
    </FilterCard>
  )
}

export default TextFilter
